import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Paper } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAuth } from '../contexts/AuthContext';
import { useMFA } from '../contexts/MFAContext';
import { MFATypes } from '@flashbacktech/flashbackclient/api';

/**
 * Handles the magic link clicked from email.
 * - If user is authenticated: post-login MFA verification (verify token, set session, redirect to hub).
 * - If not authenticated: first-time activation (POST /mfa/magic-link/activate).
 */
const MagicLinkActivatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, authState } = useAuth();
  const { verifyMFALogin } = useMFA();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Invalid link: missing token.');
      return;
    }
    if (ranRef.current) return;
    ranRef.current = true;

    const isPostLoginVerification = isAuthenticated && !!authState.token;

    if (isPostLoginVerification) {
      // Post-login MFA verification: user already logged in, complete MFA and bypass post-login screen
      verifyMFALogin({
        mfaType: MFATypes.MFAType.MAGIC_LINK,
        token,
      })
        .then((response) => {
          if (response?.success) {
            try {
              sessionStorage.setItem('mfa_verified_token', authState.token || '');
              sessionStorage.setItem('mfa_verified_via_magic_link', '1');
            } catch {
              // ignore
            }
            setStatus('success');
            setMessage('Verification successful. Taking you to the app...');
            navigate('/hub', { replace: true });
          } else {
            setStatus('error');
            setMessage((response as { error?: string })?.error || 'Verification failed.');
          }
        })
        .catch(() => {
          setStatus('error');
          setMessage('Verification failed. Please try again.');
        });
      return;
    }

    // First-time activation (no auth): enable Magic Link MFA method
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const activateUrl = `${apiUrl.replace(/\/$/, '')}/mfa/magic-link/activate`;

    fetch(activateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success) {
          setStatus('success');
          setMessage(data.message || 'Magic Link is now active. You can sign in with it.');
          setTimeout(() => navigate('/', { replace: true }), 2500);
        } else {
          setStatus('error');
          setMessage(data?.error || 'Activation failed.');
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage('Could not reach the server. Please try again.');
      });
  }, [searchParams, navigate, isAuthenticated, authState.token, verifyMFALogin]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f9fafb',
        p: 2,
      }}
    >
      <Paper sx={{ maxWidth: 420, width: '100%', p: 4, textAlign: 'center' }}>
        {status === 'loading' && (
          <>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Activating Magic Link...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait.
            </Typography>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Magic Link activated
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting you...
            </Typography>
            <Button variant="contained" sx={{ mt: 3 }} onClick={() => navigate('/', { replace: true })}>
              Go to app
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Activation failed
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {message}
            </Typography>
            <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/login', { replace: true })}>
              Go to login
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MagicLinkActivatePage;
